import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { ThirdPartyAccessoryProvider } from '@upstart/harmony/lib/constants';
import { get } from 'lodash';
import { bindActionCreators } from 'redux';
import { Tabs, Tab } from 'react-bootstrap/lib';
import { MessageType } from '@upstart/harmony/lib/legos/websiteTypes';
import SidebarContainer from '../SidebarContainer';
import DealerNav from '../../components/DealerNav';
import LoadingOverlay from '../../components/LoadingOverlay';
import AccessoriesList from '../../components/AccessoriesList';
import CategoriesList from '../../components/CategoriesList';
import dealersActions from '../../redux/dealers/actions';
import accessoryActions from '../../redux/accessories/actions';
import { AccessoryDropDown } from './AccessoryDropdown.js';
import './Accessories.scss';
import { PageMessage } from '../../components/GlobalUI';
import ErrorMessage from '../../components/ErrorMessage';

/**
 * Returns true if dealer is supported by insignia, otherwise displays error message and returns false
 */
const isDealerSupportedByInsignia = ({ dealer, insigniaEnabledMakes }) => {
  const insigniaMakes = insigniaEnabledMakes.makes;
  const unsupportedMakes = dealer.brand?.filter(
    make => !insigniaMakes.includes(make.toLowerCase()),
  );
  if (unsupportedMakes.length > 0) {
    PageMessage({
      type: MessageType.ERROR,
      message: `Unsupported vehicle makes: ${unsupportedMakes}`,
      headline: 'This dealer has vehicles that are not supported by Insignia',
    });

    return false;
  }

  return true;
};

function Accessories({
  actions,
  accessoryConfig,
  insigniaEnabledMakes,
  dealer,
  match,
  fetching,
  error,
}) {
  const { dealerId } = match.params;

  useEffect(() => {
    actions.dealerRequest({ dealerId });
    actions.accessoryConfigRequest({ dealerId });
    actions.insigniaEnabledMakesRequest();
  }, [actions, dealerId]);

  const updateConfig = newAccessoryConfig => {
    actions.accessoryConfigUpdateRequest({
      dealerId,
      accessoryConfig: newAccessoryConfig,
    });
  };

  const thirdPartyAccessoryProvider = get(
    dealer || {},
    'featureConfig.thirdPartyAccessoryProvider',
  );

  const updateThirdPartyAccessoryProvider = async thirdParty => {
    let thirdPartyAccessoryProviderValue;

    switch (thirdParty) {
      case ThirdPartyAccessoryProvider.ShiftDigitalFTP:
        thirdPartyAccessoryProviderValue =
          ThirdPartyAccessoryProvider.ShiftDigitalFTP;
        break;
      case ThirdPartyAccessoryProvider.Insignia: {
        const supported = isDealerSupportedByInsignia({
          dealer,
          insigniaEnabledMakes,
        });
        if (supported) {
          thirdPartyAccessoryProviderValue =
            ThirdPartyAccessoryProvider.Insignia;
        }
        break;
      }
      default:
        thirdPartyAccessoryProviderValue = null;
    }

    await actions.updateDealerRequest({
      dealer: {
        id: dealer.id,
        featureConfig: {
          thirdPartyAccessoryProvider: thirdPartyAccessoryProviderValue,
        },
      },
      isDiffObject: true,
    });
  };

  return (
    <SidebarContainer title="Accessories" logo={dealer.logo}>
      <div className="dealers-table">
        <DealerNav />
        {error && <ErrorMessage error={error} />}
        <div className="accessories-container">
          <LoadingOverlay active={fetching}>
            {dealer && dealer.id && (
              <>
                <AccessoryDropDown
                  updatethirdPartyAccessoryProvider={
                    updateThirdPartyAccessoryProvider
                  }
                  thirdPartyAccessoryProvider={thirdPartyAccessoryProvider}
                />
                <Tabs id="accessories-tabs" mountOnEnter unmountOnExit>
                  <Tab eventKey="listing" title="Accessory Listing">
                    <AccessoriesList
                      dealer={dealer}
                      accessoryConfig={accessoryConfig}
                      updateConfig={updateConfig}
                    />
                  </Tab>
                  <Tab eventKey="categories" title="Accessory Categories">
                    <CategoriesList
                      accessoryConfig={accessoryConfig}
                      updateConfig={updateConfig}
                    />
                  </Tab>
                </Tabs>
              </>
            )}
          </LoadingOverlay>
        </div>
      </div>
    </SidebarContainer>
  );
}

Accessories.propTypes = {
  actions: PropTypes.object,
  accessoryConfig: PropTypes.object,
  insigniaEnabledMakes: PropTypes.object,
  dealer: PropTypes.object,
  fetching: PropTypes.bool,
  match: PropTypes.object,
};

const mapStateToProps = ({ accessories, dealers }) => ({
  dealer: dealers.dealer,
  error: dealers.error || accessories.error,
  accessoryConfig: accessories.accessoryConfig,
  insigniaEnabledMakes: accessories.insigniaEnabledMakes,
  fetching: dealers.fetching || accessories.fetching,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    { ...dealersActions, ...accessoryActions },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(Accessories);

export const test = {
  isDealerSupportedByInsignia,
};
